/**
 * en-GB messages added by Phase 4 task W31 (docs/PHASE-4-PLAN.md). Only that task edits this file.
 * Flat keys, `{name}` placeholders, merged into enGB by ./index.ts.
 */
export const w31EnGB = {
    // --- Getting-started wizard ---------------------------------------------------------------------
    'setup.wizard.title': 'Set up your organisation',
    'setup.wizard.description': 'A few quick steps to get your first site, groups and sign-in tablet ready. You can come back to this at any time.',
    'setup.wizard.progress': '{done} of {total} steps finished',
    'setup.wizard.complete': 'Set-up is complete. You can still change anything below.',
    'setup.wizard.dismiss': 'Hide this wizard',
    'setup.wizard.backToStart': 'Back to getting started',
    'setup.wizard.stepTitle': 'Step {n}: {title}',
    'setup.wizard.status.todo': 'To do',
    'setup.wizard.status.done': 'Done',
    'setup.wizard.status.skipped': 'Skipped',
    'setup.wizard.markDone': 'Mark as done',
    'setup.wizard.skip': 'Skip for now',
    'setup.wizard.step.branding': 'Add your branding',
    'setup.wizard.step.brandingHelp': 'Your logo and colours appear on the portal, the sign-in tablet and visitor emails.',
    'setup.wizard.step.site': 'Create your first site',
    'setup.wizard.step.siteHelp': 'A site is a building or location where people sign in.',
    'setup.wizard.step.groups': 'Choose your groups',
    'setup.wizard.step.groupsHelp': 'Groups decide who signs in and what they are asked. Start from a ready-made group and change it later.',
    'setup.wizard.step.kiosk': 'Pair a sign-in tablet',
    'setup.wizard.step.kioskHelp': 'Get a code, then enter it on the tablet to link it to your site.',
    'setup.wizard.step.users': 'Invite your team',
    'setup.wizard.step.usersHelp': 'Add the receptionists and administrators who will use CloudSign.',
    'setup.wizard.branding.open': 'Open branding settings',
    'setup.wizard.users.open': 'Open users',
    'setup.wizard.site.name': 'Site name',
    'setup.wizard.site.address': 'Address',
    'setup.wizard.site.timeZone': 'Time zone',
    'setup.wizard.site.create': 'Create site',
    'setup.wizard.site.done': 'Your first site has been created.',
    'setup.wizard.groups.needSite': 'Create your first site before choosing groups.',
    'setup.wizard.groups.legend': 'Groups to create at this site',
    'setup.wizard.groups.visitors': 'Visitors',
    'setup.wizard.groups.visitorsHelp': 'People who visit and are met by a host.',
    'setup.wizard.groups.staff': 'Staff',
    'setup.wizard.groups.staffHelp': 'Your own employees.',
    'setup.wizard.groups.contractors': 'Contractors',
    'setup.wizard.groups.contractorsHelp': 'People who work on site for a while.',
    'setup.wizard.groups.create': 'Create groups',
    'setup.wizard.groups.again': 'Add the chosen groups',
    'setup.wizard.groups.done': 'Your starter groups are in place. Choosing the same ones again will not create duplicates.',
    'setup.wizard.groups.result': '{created} created, {existing} already there.',
    'setup.wizard.kiosk.needSite': 'Create your first site before pairing a tablet.',
    'setup.wizard.kiosk.name': 'Tablet name',
    'setup.wizard.kiosk.defaultName': 'Reception tablet',
    'setup.wizard.kiosk.getCode': 'Get pairing code',
    'setup.wizard.kiosk.codeLabel': 'Pairing code: {code}',
    'setup.wizard.kiosk.codeHelp': 'Enter this code in the CloudSign Kiosk app on the tablet. It works once and expires soon.',
    'setup.wizard.kiosk.done': 'A tablet is already paired.',
    // --- Custom domains -----------------------------------------------------------------------------
    'setup.domains.title': 'Custom domains',
    'setup.domains.description': 'Use your own web address, such as signin.yourcompany.co.uk, for the portal and your visitor pages.',
    'setup.domains.addTitle': 'Add a domain',
    'setup.domains.addHelp': 'After adding it, create the DNS records shown below at your domain provider, then check it.',
    'setup.domains.hostname': 'Domain name',
    'setup.domains.hostnameHint': 'For example signin.yourcompany.co.uk',
    'setup.domains.add': 'Add domain',
    'setup.domains.empty': 'No custom domains yet. Add one above.',
    'setup.domains.primary': 'Primary',
    'setup.domains.status.pending_dns': 'Waiting for DNS',
    'setup.domains.status.verified': 'Verified',
    'setup.domains.status.failed': 'Check failed',
    'setup.domains.status.disabled': 'Disabled',
    'setup.domains.kind.subdomain': 'CloudSign address',
    'setup.domains.kind.custom': 'Your own domain',
    'setup.domains.verifiedAt': 'Verified {time}',
    'setup.domains.checkedAt': 'Last checked {time}',
    'setup.domains.notChecked': 'Not checked yet',
    'setup.domains.dnsHelp': 'Add these records at your domain provider. DNS changes can take a while to spread.',
    'setup.domains.txtName': 'TXT record name',
    'setup.domains.txtValue': 'TXT record value',
    'setup.domains.cname': 'CNAME record points to',
    'setup.domains.checkNow': 'Check now',
    'setup.domains.makePrimary': 'Make primary',
    'setup.domains.remove': 'Remove',
    'setup.domains.removeConfirm': 'Remove domain',
    // --- ID checks ----------------------------------------------------------------------------------
    'setup.idc.title': 'ID checks',
    'setup.idc.description': 'Check a visitor\'s ID document on the sign-in tablet. Off by default. Only the result is kept unless you choose to store the image.',
    'setup.idc.dpia.title': 'Data protection impact assessment',
    'setup.idc.dpia.description': 'Checking ID documents is high-risk use of personal data. Before any group can use it, confirm that your organisation has completed a data protection impact assessment (DPIA).',
    'setup.idc.dpia.notConfirmed': 'Not confirmed',
    'setup.idc.dpia.confirmed': 'Confirmed',
    'setup.idc.dpia.explain': 'CloudSign reads the document on the tablet and keeps only the document type, whether it is in date, whether the name matches, and the outcome. Nothing else from the document is sent to the server.',
    'setup.idc.dpia.reference': 'DPIA reference',
    'setup.idc.dpia.referenceHint': 'The name or reference of your own DPIA document.',
    'setup.idc.dpia.tick': 'I confirm that my organisation has completed a DPIA for checking ID documents.',
    'setup.idc.dpia.confirm': 'Confirm DPIA',
    'setup.idc.dpia.by': 'Confirmed by',
    'setup.idc.dpia.on': 'Confirmed on',
    'setup.idc.dpia.withdrawWarning': 'Withdrawing the confirmation switches ID checks off for every group and deletes any stored document images.',
    'setup.idc.dpia.withdraw': 'Withdraw confirmation',
    'setup.idc.dpia.withdrawConfirm': 'Withdraw and switch everything off',
    'setup.idc.retention.title': 'Keeping results',
    'setup.idc.retention.description': 'How long the yes or no result is kept.',
    'setup.idc.groupsHint': 'Switch ID checks on for a group from that group\'s page, under Settings.',
    'setup.idc.group.title': 'ID check',
    'setup.idc.group.description': 'Ask people in this group to show an ID document on the sign-in tablet.',
    'setup.idc.group.locked': 'ID checks cannot be switched on until your organisation has confirmed its DPIA.',
    'setup.idc.group.dpiaLink': 'Go to ID check settings',
    'setup.idc.group.dpiaRequired': 'This was not saved. Confirm your DPIA before switching on ID checks or storing images.',
    'setup.idc.group.mode': 'ID check',
    'setup.idc.group.mode.off': 'Off',
    'setup.idc.group.mode.optional': 'Optional (reception can skip)',
    'setup.idc.group.mode.required': 'Required',
    'setup.idc.group.documents': 'Accepted documents',
    'setup.idc.doc.passport': 'Passport',
    'setup.idc.doc.driving_licence': 'Driving licence',
    'setup.idc.doc.national_id': 'National ID card',
    'setup.idc.group.onFail': 'If the check fails',
    'setup.idc.group.onFail.warn': 'Warn reception and let them in',
    'setup.idc.group.onFail.refuse': 'Do not sign them in',
    'setup.idc.group.onFailHint': 'A check fails when the document is out of date or the name does not match.',
    'setup.idc.group.repeat': 'Ask again after (days)',
    'setup.idc.group.repeatHint': '0 asks at every visit.',
    'setup.idc.group.storeImage': 'Keep a photo of the document',
    'setup.idc.group.storeImageHint': 'Off by default. The photo is stored encrypted and deleted automatically after the number of days below.',
    'setup.idc.group.imageDays': 'Delete photos after (days)',
    'setup.idc.group.imageDaysHint': 'Between 1 and 90 days.',
    // --- Wallet ---------------------------------------------------------------------------------------
    'setup.wallet.title': 'Wallet pass',
    'setup.wallet.description': 'Let visitors and staff add their sign-in code to Google Wallet.',
    'setup.wallet.tenant.title': 'Google Wallet',
    'setup.wallet.tenant.description': 'Choose who is offered an "Add to Google Wallet" button.',
    'setup.wallet.tenant.forVisitors': 'Offer to visitors',
    'setup.wallet.tenant.forVisitorsHint': 'Shown on the pre-arrival page.',
    'setup.wallet.tenant.forMembers': 'Offer to staff and members',
    'setup.wallet.tenant.forMembersHint': 'Shown in the Companion app.',
    'setup.wallet.tenant.cardTitle': 'Card title',
    'setup.wallet.tenant.cardTitleHint': 'Leave empty to use your organisation\'s name.',
    'setup.wallet.platform.title': 'Wallet pass credentials',
    'setup.wallet.platform.description': 'The Google Wallet issuer account shared by every customer.',
    'setup.wallet.platform.google': 'Google Wallet issuer',
    'setup.wallet.platform.googleHelp': 'Saved separately from the other platform settings. The private key is stored encrypted and is never shown again.',
    'setup.wallet.platform.live': 'Sending to Google',
    'setup.wallet.platform.notLive': 'Not sending to Google',
    'setup.wallet.platform.keySet': 'Private key saved',
    'setup.wallet.platform.keyNotSet': 'No private key',
    'setup.wallet.platform.incomplete': 'Passes are only sent to Google once the issuer ID, service account email and private key are all set.',
    'setup.wallet.platform.provider': 'Mode',
    'setup.wallet.platform.providerHint': 'Test mode records what would be sent and gives a dummy link. Off hides the buttons everywhere.',
    'setup.wallet.platform.provider.off': 'Off',
    'setup.wallet.platform.provider.log': 'Test mode (nothing is sent)',
    'setup.wallet.platform.provider.google': 'Google Wallet',
    'setup.wallet.platform.issuerId': 'Issuer ID',
    'setup.wallet.platform.email': 'Service account email',
    'setup.wallet.platform.privateKey': 'Private key (PEM)',
    'setup.wallet.platform.privateKeyHint': 'Paste a new key to replace the saved one. Leave empty to keep it.',
    'setup.wallet.platform.keyPlaceholderSet': 'A key is saved. Paste a new one to replace it.',
    // --- Languages ------------------------------------------------------------------------------------
    'setup.lang.draftOption': '{name} (draft)',
    'setup.lang.site.title': 'Languages',
    'setup.lang.site.description': 'The languages visitors see on the sign-in tablet and your visitor pages.',
    'setup.lang.site.default': 'Default language',
    'setup.lang.site.defaultHint': 'Used when a visitor has not chosen another.',
    'setup.lang.site.offered': 'Languages visitors can choose',
    'setup.lang.site.offeredHint': 'Tick two or more to show a language button. Your own wording, such as group names and questions, is shown as you wrote it.',
    'setup.lang.group.title': 'Default language',
    'setup.lang.group.description': 'Start this group\'s sign-in and emails in a particular language.',
    'setup.lang.group.default': 'Default language',
    'setup.lang.group.defaultHint': 'Visitors can still change language if the site offers more than one.',
    'setup.lang.group.followSite': 'Same as the site',
};
//# sourceMappingURL=w31.js.map